import { MANUAL_REVIEW_SCOPE } from "components/manualReview/hotkeyScopes";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useCallback, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

const CYCLE_HOTKEY_OPTIONS = {
  scopes: [MANUAL_REVIEW_SCOPE],
  preventDefault: true,
  enableOnFormTags: false,
};

type TargetedReviewFootageLocation = Readonly<{
  footageLocation: string;
  canWatchOtherLocations: boolean;
}>;

/**
 * Which location's cameras the period inspector plays, and a hotkey that steps
 * through the site's labelling locations. Only the footage moves: the worklist,
 * the labels and the writable window stay with the location under review.
 */
export const useTargetedReviewFootageLocation = ({
  reviewedLocation,
}: {
  reviewedLocation: string;
}): TargetedReviewFootageLocation => {
  const { getHotkey } = useHotkeyContext();
  const { locationNames } = useLabellingLocations();

  // A URL can name a location the endpoint leaves out, such as a supporting one.
  const cycleOrder = useMemo(
    () =>
      locationNames.includes(reviewedLocation)
        ? locationNames
        : [reviewedLocation, ...locationNames],
    [locationNames, reviewedLocation]
  );

  const [chosenLocation, setChosenLocation] = useState<string | null>(null);

  const cycleFootageLocation = useCallback(() => {
    setChosenLocation((current) => {
      const currentIndex = cycleOrder.indexOf(current ?? reviewedLocation);
      const next = cycleOrder[(currentIndex + 1) % cycleOrder.length];
      return next === undefined || next === reviewedLocation ? null : next;
    });
  }, [cycleOrder, reviewedLocation]);

  const canWatchOtherLocations = cycleOrder.length > 1;
  const cycleHotkey = getHotkey("cycleFootageLocation");
  useHotkeys(
    cycleHotkey,
    cycleFootageLocation,
    { ...CYCLE_HOTKEY_OPTIONS, enabled: canWatchOtherLocations },
    [cycleHotkey, cycleFootageLocation, canWatchOtherLocations]
  );

  return {
    // The site's locations can change under a reviewer who has already chosen one.
    footageLocation:
      chosenLocation !== null && cycleOrder.includes(chosenLocation)
        ? chosenLocation
        : reviewedLocation,
    canWatchOtherLocations,
  };
};
